import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/AuthContext";
import { LoginAuthProvider } from "../../hooks/LoginAuthContext";
import { SignupAuthProvider } from "../../hooks/SignupAuthContext";
import { Logo } from "../shared/brand";
import Typography from "../shared/typography";
import LoginForm from "./login-form";
import SignupForm from "./signup-form";

function AuthContainer({
  defaultIsLoginView = true,
}: {
  defaultIsLoginView?: boolean;
}) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [isLoginView, setIsLoginView] = useState(defaultIsLoginView);

  const toggleView = () => {
    setIsLoginView(!isLoginView);
  };

  // Redirect to home or previous location if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || "/";
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  return (
    <div className="flex flex-col items-center justify-center h-full min-h-screen p-4 pt-2 bg-gradient-to-b from-cream to-terracotta">
      <div className="w-full max-w-md mb-8 text-center">
        <div className="flex justify-center mb-4">
          <Logo size="md" />
        </div>
        <Typography variant="h2" className="mb-2 text-primary">
          Welcome to Harper
        </Typography>
        <Typography variant="body1" className="text-terracotta">
          {isLoginView
            ? "Sign in to your account to continue"
            : "Create an account to get started"}
        </Typography>
      </div>

      <AnimatePresence mode="wait">
        {isLoginView ? (
          <motion.div
            key="login"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="w-full"
          >
            <LoginAuthProvider>
              <LoginForm onToggleView={toggleView} />
            </LoginAuthProvider>
          </motion.div>
        ) : (
          <motion.div
            key="signup"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="w-full"
          >
            <SignupAuthProvider>
              <SignupForm onToggleView={toggleView} />
            </SignupAuthProvider>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="w-full max-w-md mt-8 text-center">
        <Typography variant="caption" className="text-cream">
          © {new Date().getFullYear()} Harper. All rights reserved.
        </Typography>
      </div>
    </div>
  );
}

export default AuthContainer;
